/**
 * Iter 95 (Fev 2026) - Page Direction > Equipe
 *
 * Permet a Jean / Charles / Amelie d'inviter, modifier, reinitialiser le mot de passe
 * ou supprimer les membres de l'equipe sans toucher au code.
 *
 * Acces :
 *  - Jean / Amelie (cross-branch) : voient + gerent les 2 branches
 *  - Charles : voit + gere uniquement les utilisateurs branche=nord
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLE_OPTIONS = [
  { value: 'ceo', label: 'CEO / Direction' },
  { value: 'julie', label: 'Projets' },
  { value: 'carla', label: 'CRM' },
  { value: 'amelie', label: 'Délais' },
  { value: 'collaborateur', label: 'Collaborateur' },
  { value: 'courtier', label: 'Courtier partenaire' },
  { value: 'artisan', label: 'Artisan' },
  { value: 'admin', label: 'Administrateur' },
];

const ROLE_COLORS = {
  ceo: 'bg-amber-100 text-amber-800 border-amber-300',
  julie: 'bg-blue-100 text-blue-800 border-blue-300',
  carla: 'bg-purple-100 text-purple-800 border-purple-300',
  amelie: 'bg-pink-100 text-pink-800 border-pink-300',
  collaborateur: 'bg-emerald-100 text-emerald-800 border-emerald-300',
  courtier: 'bg-indigo-100 text-indigo-800 border-indigo-300',
  artisan: 'bg-orange-100 text-orange-800 border-orange-300',
  admin: 'bg-rose-100 text-rose-800 border-rose-300',
};

const BRANCH_LABELS = { bretagne: 'Bretagne', nord: 'Nord' };

/** Modale d'invitation */
function InviteModal({ open, onClose, onInvited, currentUser }) {
  const canCrossBranch = !!currentUser?.cross_branch_access;
  const defaultBranch = currentUser?.branch || 'bretagne';
  const [form, setForm] = useState({
    email: '', name: '', role: 'collaborateur',
    branch: defaultBranch, cross_branch_access: false, send_email: true,
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setForm({
        email: '', name: '', role: 'collaborateur',
        branch: defaultBranch, cross_branch_access: false, send_email: true,
      });
    }
  }, [open, defaultBranch]);

  if (!open) return null;
  const submit = async () => {
    if (!form.email || !form.name) {
      toast.error('Email et nom sont obligatoires.');
      return;
    }
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/team/invite`, form);
      toast.success('Invitation créée');
      onInvited(r.data);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de l'invitation");
    } finally {
      setSubmitting(false);
    }
  };
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" data-testid="team-invite-modal">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6">
        <h3 className="text-lg font-bold text-gray-900 mb-4">Inviter un membre</h3>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-semibold text-gray-600 uppercase">Email</label>
            <input
              type="email" value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
              placeholder="prenom.nom@real-ease.fr"
              data-testid="team-invite-email"
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-600 uppercase">Nom complet</label>
            <input
              type="text" value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
              placeholder="Tony Pegain"
              data-testid="team-invite-name"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-gray-600 uppercase">Rôle</label>
              <select
                value={form.role}
                onChange={(e) => setForm({ ...form, role: e.target.value })}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm bg-white"
                data-testid="team-invite-role"
              >
                {ROLE_OPTIONS.map((r) => (
                  <option key={r.value} value={r.value}>{r.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-600 uppercase">Branche</label>
              <select
                value={form.branch}
                onChange={(e) => setForm({ ...form, branch: e.target.value })}
                disabled={!canCrossBranch}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm bg-white disabled:bg-gray-100"
                data-testid="team-invite-branch"
              >
                <option value="bretagne">Bretagne</option>
                <option value="nord">Nord</option>
              </select>
            </div>
          </div>
          {canCrossBranch && (
            <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
              <input
                type="checkbox" checked={form.cross_branch_access}
                onChange={(e) => setForm({ ...form, cross_branch_access: e.target.checked })}
                data-testid="team-invite-cross-branch"
              />
              <span>Accès cross-branche (voit les 2 branches)</span>
            </label>
          )}
          <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
            <input
              type="checkbox" checked={form.send_email}
              onChange={(e) => setForm({ ...form, send_email: e.target.checked })}
              data-testid="team-invite-send-email"
            />
            <span>Envoyer un email d'invitation avec le mot de passe</span>
          </label>
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <button onClick={onClose} className="px-4 py-2 rounded-md text-gray-700 hover:bg-gray-100 text-sm font-medium" data-testid="team-invite-cancel">
            Annuler
          </button>
          <button
            onClick={submit} disabled={submitting}
            className="px-4 py-2 rounded-md bg-[#13736D] hover:bg-[#0e5d58] text-white text-sm font-semibold disabled:opacity-50"
            data-testid="team-invite-submit"
          >
            {submitting ? 'Envoi...' : "Créer l'invitation"}
          </button>
        </div>
      </div>
    </div>
  );
}

/** Modale apres invitation/reset : affiche le mot de passe + lien copiable */
function CredentialsModal({ data, onClose }) {
  if (!data) return null;
  const copy = (text, label) => {
    navigator.clipboard.writeText(text).then(
      () => toast.success(`${label} copié`),
      () => toast.error('Copie impossible'),
    );
  };
  const email = data.user?.email || data.email;
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" data-testid="team-credentials-modal">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6">
        <div className="flex items-center gap-2 mb-3">
          <span className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center">
            <svg className="w-4 h-4 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>
          </span>
          <h3 className="text-lg font-bold text-gray-900">Identifiants prêts</h3>
        </div>
        <p className="text-sm text-gray-600 mb-4">
          {data.email_sent
            ? "Un email a été envoyé à l&apos;utilisateur. Vous pouvez également copier ces identifiants pour les transmettre manuellement (les liens dans les emails peuvent être bloqués)."
            : "L&apos;envoi automatique a échoué — copiez et transmettez ces identifiants manuellement."}
        </p>
        <div className="space-y-2">
          <div className="flex items-center justify-between gap-2 bg-gray-50 border border-gray-200 rounded-md px-3 py-2">
            <div className="min-w-0">
              <p className="text-[10px] uppercase text-gray-500 font-semibold">Email</p>
              <p className="text-sm font-mono text-gray-800 truncate">{email}</p>
            </div>
            <button onClick={() => copy(email, 'Email')} className="text-xs px-2 py-1 rounded bg-white border border-gray-300 hover:bg-gray-50" data-testid="team-creds-copy-email">Copier</button>
          </div>
          <div className="flex items-center justify-between gap-2 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
            <div className="min-w-0">
              <p className="text-[10px] uppercase text-amber-700 font-semibold">Mot de passe temporaire</p>
              <p className="text-sm font-mono text-amber-900 truncate">{data.temp_password}</p>
            </div>
            <button onClick={() => copy(data.temp_password, 'Mot de passe')} className="text-xs px-2 py-1 rounded bg-white border border-amber-300 hover:bg-amber-100" data-testid="team-creds-copy-password">Copier</button>
          </div>
          <div className="flex items-center justify-between gap-2 bg-blue-50 border border-blue-200 rounded-md px-3 py-2">
            <div className="min-w-0">
              <p className="text-[10px] uppercase text-blue-700 font-semibold">Lien de connexion</p>
              <p className="text-sm font-mono text-blue-900 truncate">{data.login_url}</p>
            </div>
            <button onClick={() => copy(data.login_url, 'Lien')} className="text-xs px-2 py-1 rounded bg-white border border-blue-300 hover:bg-blue-100" data-testid="team-creds-copy-link">Copier</button>
          </div>
        </div>
        <div className="flex justify-end mt-5">
          <button onClick={onClose} className="px-4 py-2 rounded-md bg-[#13736D] hover:bg-[#0e5d58] text-white text-sm font-semibold" data-testid="team-creds-close">
            Fermer
          </button>
        </div>
      </div>
    </div>
  );
}

export default function TeamManagementPage() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [me, setMe] = useState(null);
  const [showInvite, setShowInvite] = useState(false);
  const [credsData, setCredsData] = useState(null);
  const [branchFilter, setBranchFilter] = useState('all');

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [usersR, meR] = await Promise.all([
        axios.get(`${API}/team/users`),
        axios.get(`${API}/auth/me`),
      ]);
      setUsers(usersR.data.users || []);
      setMe(meR.data || null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur de chargement");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const onInvited = (data) => {
    setCredsData(data);
    fetchAll();
  };

  const resetPassword = async (u) => {
    if (!window.confirm(`Réinitialiser le mot de passe de ${u.name} (${u.email}) ?`)) return;
    try {
      const r = await axios.post(`${API}/team/users/${u.user_id}/reset-password`, { send_email: true });
      setCredsData({ ...r.data, user: u });
      toast.success('Mot de passe réinitialisé');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    }
  };

  const removeUser = async (u) => {
    if (!window.confirm(`Supprimer définitivement ${u.name} (${u.email}) ? Cette action est irréversible.`)) return;
    try {
      await axios.delete(`${API}/team/users/${u.user_id}`);
      toast.success('Utilisateur supprimé');
      fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    }
  };

  const updateUser = async (userId, patch) => {
    try {
      const r = await axios.put(`${API}/team/users/${userId}`, patch);
      setUsers((prev) => prev.map((u) => (u.user_id === userId ? r.data.user : u)));
      toast.success('Mis à jour');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    }
  };

  const canCross = !!me?.cross_branch_access;
  const branches = canCross ? ['all', 'bretagne', 'nord'] : [me?.branch || 'bretagne'];
  const filtered = branchFilter === 'all' ? users : users.filter((u) => u.branch === branchFilter);

  return (
    <div className="max-w-7xl mx-auto p-6" data-testid="team-management-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900" style={{ fontFamily: 'Georgia, serif' }}>Équipe</h1>
          <p className="text-sm text-gray-600 mt-1">
            {canCross ? "Gérez les collaborateurs de toutes les branches RealEase." : `Gérez les collaborateurs de la branche ${BRANCH_LABELS[me?.branch] || ''}.`}
          </p>
        </div>
        <button
          onClick={() => setShowInvite(true)}
          className="flex items-center gap-2 px-4 py-2 rounded-md bg-[#13736D] hover:bg-[#0e5d58] text-white text-sm font-semibold shadow"
          data-testid="team-invite-button"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" /></svg>
          Inviter un membre
        </button>
      </div>

      {canCross && (
        <div className="flex gap-2 mb-4">
          {branches.map((b) => (
            <button
              key={b}
              onClick={() => setBranchFilter(b)}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition ${branchFilter === b ? 'bg-[#13736D] text-white border-[#13736D]' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}
              data-testid={`team-branch-filter-${b}`}
            >
              {b === 'all' ? 'Toutes' : BRANCH_LABELS[b] || b}
            </button>
          ))}
        </div>
      )}

      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-500 text-sm">Chargement...</div>
        ) : filtered.length === 0 ? (
          <div className="p-8 text-center text-gray-500 text-sm">Aucun collaborateur.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left px-4 py-2 font-semibold text-xs text-gray-600 uppercase">Nom</th>
                <th className="text-left px-4 py-2 font-semibold text-xs text-gray-600 uppercase">Email</th>
                <th className="text-left px-4 py-2 font-semibold text-xs text-gray-600 uppercase">Rôle</th>
                <th className="text-left px-4 py-2 font-semibold text-xs text-gray-600 uppercase">Branche</th>
                <th className="text-left px-4 py-2 font-semibold text-xs text-gray-600 uppercase">Cross</th>
                <th className="text-right px-4 py-2 font-semibold text-xs text-gray-600 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((u) => {
                const isSelf = u.user_id === me?.user_id;
                return (
                  <tr key={u.user_id} className="border-b border-gray-100 hover:bg-gray-50" data-testid={`team-user-row-${u.user_id}`}>
                    <td className="px-4 py-2">
                      <div className="flex items-center gap-2">
                        {u.avatar_url ? (
                          <img src={u.avatar_url} alt={u.name} className="w-7 h-7 rounded-full object-cover" />
                        ) : (
                          <div className="w-7 h-7 rounded-full bg-[#13736D] text-white flex items-center justify-center text-xs font-bold">
                            {(u.name || '?').charAt(0).toUpperCase()}
                          </div>
                        )}
                        <span className="font-medium text-gray-900">{u.name}</span>
                        {isSelf && <span className="text-[10px] text-gray-400 italic">(vous)</span>}
                      </div>
                    </td>
                    <td className="px-4 py-2 text-gray-700">{u.email}</td>
                    <td className="px-4 py-2">
                      <select
                        value={u.role}
                        onChange={(e) => updateUser(u.user_id, { role: e.target.value })}
                        disabled={isSelf}
                        className={`text-xs font-semibold px-2 py-1 rounded border ${ROLE_COLORS[u.role] || 'bg-gray-100 text-gray-800 border-gray-300'} disabled:opacity-60`}
                        data-testid={`team-user-role-${u.user_id}`}
                      >
                        {ROLE_OPTIONS.map((r) => (
                          <option key={r.value} value={r.value}>{r.label}</option>
                        ))}
                      </select>
                    </td>
                    <td className="px-4 py-2">
                      {canCross ? (
                        <select
                          value={u.branch}
                          onChange={(e) => updateUser(u.user_id, { branch: e.target.value })}
                          disabled={isSelf}
                          className="text-xs font-semibold px-2 py-1 rounded border border-gray-300 bg-white disabled:opacity-60"
                          data-testid={`team-user-branch-${u.user_id}`}
                        >
                          <option value="bretagne">Bretagne</option>
                          <option value="nord">Nord</option>
                        </select>
                      ) : (
                        <span className="text-xs font-semibold px-2 py-1 rounded bg-gray-100 text-gray-800 border border-gray-300">{BRANCH_LABELS[u.branch] || u.branch}</span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-center">
                      {canCross ? (
                        <input
                          type="checkbox" checked={u.cross_branch_access}
                          onChange={(e) => updateUser(u.user_id, { cross_branch_access: e.target.checked })}
                          disabled={isSelf}
                          data-testid={`team-user-cross-${u.user_id}`}
                        />
                      ) : (
                        u.cross_branch_access ? '✓' : '—'
                      )}
                    </td>
                    <td className="px-4 py-2 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => resetPassword(u)}
                          title="Réinitialiser le mot de passe"
                          className="text-xs px-2 py-1 rounded bg-amber-50 text-amber-700 hover:bg-amber-100 border border-amber-200"
                          data-testid={`team-user-reset-${u.user_id}`}
                        >
                          Reset MDP
                        </button>
                        {!isSelf && (
                          <button
                            onClick={() => removeUser(u)}
                            title="Supprimer"
                            className="text-xs px-2 py-1 rounded bg-red-50 text-red-700 hover:bg-red-100 border border-red-200"
                            data-testid={`team-user-delete-${u.user_id}`}
                          >
                            Suppr.
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <InviteModal
        open={showInvite}
        onClose={() => setShowInvite(false)}
        onInvited={onInvited}
        currentUser={me}
      />
      <CredentialsModal data={credsData} onClose={() => setCredsData(null)} />
    </div>
  );
}
